import type { UserManagerSettings } from 'oidc-client-ts';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { type ApiAuthenticationProviders, type AppSpec, getIzimiMetadataHeaders } from '@izimi/api';
import { appEnv } from '@izimi/core';

const AUTHENTICATION_PROVIDER_NAME_STORAGE_KEY = 'auth:authenticationProvider';

const userStorageKey = `auth:${appEnv}`;
const userStoragePrefix = 'oidc.';
const isLocalhost = window.location.hostname === 'localhost';
export const browserStorage = window.sessionStorage;

export const getDataFromState = ({
  state,
  key,
}: {
  state: unknown;
  key: string;
}): string | null => {
  if (typeof state !== 'string') {
    return null;
  }

  try {
    const parsedState = JSON.parse(state);
    return parsedState != null && typeof parsedState === 'object'
      ? (parsedState[key] ?? null)
      : null;
  } catch {
    return null;
  }
};

export const getUserFromStorage = () => {
  const oidcStorage = browserStorage.getItem(`${userStoragePrefix}${userStorageKey}`);

  if (!oidcStorage) {
    return undefined;
  }

  const user = User.fromStorageString(oidcStorage);
  if (user.expired) {
    browserStorage.removeItem(`${userStoragePrefix}${userStorageKey}`);
    return undefined;
  }
  return user;
};

export const getStoredAuthenticationProviderName = () =>
  browserStorage.getItem(AUTHENTICATION_PROVIDER_NAME_STORAGE_KEY);
export const setStoredAuthenticationProviderName = (
  authenticationProviderName: ApiAuthenticationProviders[number]['providerName'],
) => browserStorage.setItem(AUTHENTICATION_PROVIDER_NAME_STORAGE_KEY, authenticationProviderName);

export const getSessionExpiredFromStorage = () => {
  const key = `auth:sessionExpired`;

  const sessionExpired = browserStorage.getItem(key);

  return {
    sessionExpired: sessionExpired === 'true',
    removeSessionExpiredFromStorage: () => browserStorage.removeItem(key),
  };
};

export const createUserManager = (
  authenticationProvider: ApiAuthenticationProviders[number],
  apiUrl: string,
  loadUserInfo: boolean = true,
  appSpec?: AppSpec,
) => {
  const authority = authenticationProvider.loginUrl;
  const isWebOnlyRoute = window.location.pathname.startsWith('/web');

  let redirectUrl = getDefaultRedirectUrl({ apiUrl });

  if (isWebOnlyRoute) {
    redirectUrl += '/web';
  }

  const tokenEndpoint = isLocalhost
    ? authenticationProvider.tokenEndpoint.replace(apiUrl, '')
    : authenticationProvider.tokenEndpoint;
  const userInfoEndpoint = `${isLocalhost ? '' : apiUrl}/api/user`;
  const endSessionEndpoint = `${apiUrl}${authenticationProvider.logoutUrl}`;

  const izimiMetadataHeaders = appSpec && getIzimiMetadataHeaders({ appSpec });

  const userManagerSettings: UserManagerSettings = {
    getUserStoreKey: () => userStorageKey,
    automaticSilentRenew: false,
    scope: authenticationProvider.scope,
    client_id: authenticationProvider.clientId,
    client_secret: authenticationProvider.clientSecret,
    redirect_uri: redirectUrl,
    authority,
    metadata: {
      authorization_endpoint: authority,
      token_endpoint: tokenEndpoint,
      userinfo_endpoint: userInfoEndpoint,
      end_session_endpoint: endSessionEndpoint,
    },
    ...(izimiMetadataHeaders && {
      extraHeaders: izimiMetadataHeaders,
    }),
  };

  return new UserManager({
    loadUserInfo,
    userStore: new WebStorageStateStore({
      prefix: userStoragePrefix,
      store: browserStorage,
    }),
    ...userManagerSettings,
  });
};

export const hasAuthParams = (location = window.location) => {
  let searchParams = new URLSearchParams(location.search);
  if ((searchParams.get('code') || searchParams.get('error')) && searchParams.get('state')) {
    return true;
  }
  searchParams = new URLSearchParams(location.hash.replace('#', '?'));
  if ((searchParams.get('code') || searchParams.get('error')) && searchParams.get('state')) {
    return true;
  }
  return false;
};

export const normalizeErrorFn = (fallbackMessage: string) => (error: Error) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(fallbackMessage);
};

export const unsupportedEnvironment = (fnName: string) => () => {
  throw new Error(`UserManager#${fnName} was called from an unsupported context.`);
};

export const getDefaultRedirectUrl = ({ apiUrl }: { apiUrl: string }) =>
  appEnv === 'local' || appEnv === 'development' || appEnv === 'test'
    ? window.location.origin
    : apiUrl;
